import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface ApproveVersionParams {
  versionId: string;
  policyId: string;
  isSubstantive: boolean;
  calculatedNextReview: string | null;
  reviewCadenceMonths: number | null;
}

interface RejectVersionParams {
  versionId: string;
  policyId: string;
  rejectionReason: string;
}

export function useApproveVersion() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ versionId, isSubstantive, calculatedNextReview, reviewCadenceMonths }: ApproveVersionParams) => {
      if (!profile) throw new Error('Not authenticated');

      // Single atomic RPC call — version update, policy update, and audit log
      const { data, error } = await supabase.rpc('approve_policy_version', {
        p_version_id: versionId,
        p_approved_by: profile.id,
        p_is_substantive: isSubstantive,
        p_calculated_next_review: calculatedNextReview ?? null,
        p_review_cadence_months: reviewCadenceMonths ?? null,
      });

      if (error) throw error;

      return { versionId, isSubstantive };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['policy-versions', variables.policyId] });
      queryClient.invalidateQueries({ queryKey: ['policy', variables.policyId] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['pending-acknowledgements'] });
      queryClient.invalidateQueries({ queryKey: ['policy-active-draft', variables.policyId] });
      
      const message = data.isSubstantive
        ? 'Version approved. Staff will need to acknowledge the new version.'
        : 'Version approved. No staff action required.';
      
      toast({
        title: 'Version approved',
        description: message,
      });

      if (data?.isSubstantive) {
        supabase.functions
          .invoke('trigger-acknowledgment-notification')
          .catch((e) => console.error('ack notification trigger failed (cron will retry):', e));
      }
    },
    onError: (error) => {
      toast({
        title: 'Failed to approve version',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRejectVersion() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ versionId, policyId, rejectionReason }: RejectVersionParams) => {
      if (!profile) throw new Error('Not authenticated');

      // Single atomic RPC call — version update, policy status, and audit log
      const { data, error } = await supabase.rpc('reject_policy_version', {
        p_version_id: versionId,
        p_rejected_by: profile.id,
        p_rejection_reason: rejectionReason,
      });

      if (error) throw error;

      return { versionId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['policy-versions', variables.policyId] });
      queryClient.invalidateQueries({ queryKey: ['policy', variables.policyId] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['policy-active-draft', variables.policyId] });
      
      toast({
        title: 'Version rejected',
        description: 'The uploader will be notified of the rejection.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to reject version',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
